import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { collection, getDocs, orderBy, query, limit } from "firebase/firestore";
import { db } from "@/firebase/config";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Timestamp } from "firebase/firestore";

interface BlogPost {
  id: string;
  title: string;
  contentHtml: string;
  description?: string;
  thumbnailUrl: string;
  author: string;
  createdAt: Timestamp;
}

const BlogSection = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const blogsRef = collection(db, "BlogsWeb");
        const q = query(blogsRef, orderBy("createdAt", "desc"), limit(3));
        const querySnapshot = await getDocs(q);

        const blogs = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as BlogPost[];

        setBlogPosts(blogs);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-forest-green" />
      </div>
    );
  }

  return (
    <section
      id="blogs"
      className="py-20 relative"
      style={{
        backgroundColor: "white",
        backgroundImage: `
          linear-gradient(30deg, rgba(243, 244, 246, 0.4) 12%, transparent 12.5%, transparent 87%, rgba(243, 244, 246, 0.4) 87.5%, rgba(243, 244, 246, 0.4)),
          linear-gradient(150deg, rgba(243, 244, 246, 0.4) 12%, transparent 12.5%, transparent 87%, rgba(243, 244, 246, 0.4) 87.5%, rgba(243, 244, 246, 0.4)),
          linear-gradient(30deg, rgba(243, 244, 246, 0.4) 12%, transparent 12.5%, transparent 87%, rgba(243, 244, 246, 0.4) 87.5%, rgba(243, 244, 246, 0.4)),
          linear-gradient(150deg, rgba(243, 244, 246, 0.4) 12%, transparent 12.5%, transparent 87%, rgba(243, 244, 246, 0.4) 87.5%, rgba(243, 244, 246, 0.4))
        `,
        backgroundSize: "80px 140px",
        backgroundPosition: "0 0, 0 0, 40px 70px, 40px 70px",
      }}
    >
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest-green mb-6">
              Wellness Insights
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-soft-saffron to-copper-brown rounded-full mx-auto mb-6"></div>
           
          </div>

          {/* Blog Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {blogPosts.map((post) => (
              <Card
                key={post.id}
                className="group hover:scale-105 transition-all duration-300 border-0 shadow-lg hover:shadow-xl bg-cloud-white overflow-hidden"
              >
                <Link
                  to={`/blog/${post.id}`}
                  className="block"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  <div className="relative">
                    <img
                      src={post.thumbnailUrl || "/placeholder.svg"}
                      alt={post.title}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-cloud-white text-forest-green px-3 py-1 rounded-full text-sm font-medium">
                        By {post.author}
                      </span>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    <h3 className="font-heading text-xl font-semibold text-forest-green mb-3 group-hover:text-copper-brown transition-colors duration-300">
                      {post.title}
                    </h3>

                    <p className="text-copper-brown leading-relaxed mb-4">
                      {post.description ||
                        post.contentHtml
                          .substring(0, 150)
                          .replace(/<[^>]*>/g, "")}
                      ...
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-soft-saffron font-medium hover:text-copper-brown transition-colors">
                        Read More →
                      </span>
                      <div className="w-0 h-1 bg-gradient-to-r from-soft-saffron to-copper-brown rounded-full group-hover:w-8 transition-all duration-300"></div>
                    </div>
                  </CardContent>
                </Link>
              </Card>
            ))}
          </div>

          {/* Explore More Button */}
          <div className="text-center">
            <Link to="/blogs" onClick={() => window.scrollTo(0, 0)}>
              <Button
                size="lg"
                className="bg-transparent border-2 border-forest-green text-forest-green hover:bg-forest-green hover:text-cloud-white px-8 py-4 rounded-full text-lg transition-all duration-300"
              >
                Explore More Articles
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
